/**
 * LaunchDarkly wiring for the marketing site.
 *
 * The booking app (`app.getdimension.com`) evaluates flags with the Node *server*
 * SDK inside Cloud Functions and proxies results over `POST /feature-flags/check-flag`.
 * This site deliberately does not reuse that: its pages are statically generated and
 * served from the Firebase CDN, so a per-request server evaluation would mean giving
 * up static caching on our top organic landing pages. We evaluate in the browser
 * instead, which needs a LaunchDarkly *client-side ID* — a different credential from
 * the same LD project, not the `sdk-…` server key the Cloud Functions use.
 */
import type { LDContext } from 'launchdarkly-js-client-sdk'

/**
 * Client-side ID for the LD environment this build targets. Public by design:
 * Next inlines every `NEXT_PUBLIC_*` var into the browser bundle, so this is the
 * *only* LaunchDarkly credential that may ever live here. Never put an `sdk-…`
 * server key in a `NEXT_PUBLIC_*` var — it would ship in view-source and grants
 * read access to every flag, segment, and targeting rule in the environment.
 */
export const LAUNCHDARKLY_CLIENT_ID = process.env.NEXT_PUBLIC_LAUNCHDARKLY_CLIENT_ID ?? ''

/**
 * Flag keys in their original LaunchDarkly form. `useCamelCaseFlagKeys` is turned
 * off in the provider so these match the dashboard exactly and Code References works.
 */
export const FLAG_PAINT_DISPOSAL_CHAT = 'paint-disposal-n8n-chatbot'
export const FLAG_OIL_RECYCLING_CHAT = 'oil-recycling-n8n-chatbot'

/**
 * True only under `next dev` (i.e. `yarn dev` on a developer's machine).
 *
 * `NODE_ENV` is set by Next itself and is hard-coded to `'production'` in anything
 * `next build` emits, so this is guaranteed false in every deployed build — unlike
 * `NEXT_PUBLIC_ENV`, which is just a value in a committed `.env` file. Flags gated on
 * this are forced on locally so the feature can be worked on without a LaunchDarkly
 * targeting rule, while deployed environments still evaluate the flag normally.
 */
export const IS_LOCAL_DEV = process.env.NODE_ENV !== 'production'

/**
 * Crawlers and synthetic agents are pinned to the control variant. Three reasons:
 * the DOM Googlebot renders stays stable on a page we're actively trying to rank,
 * bot traffic stays out of the experiment numbers, and — since LaunchDarkly bills
 * client-side usage by monthly active contexts — they stay off the bill.
 */
const CRAWLER_UA =
  /bot|crawler|spider|crawling|googlebot|bingbot|duckduckbot|slurp|baiduspider|yandex|ahrefs|semrush|petalbot|applebot|facebookexternalhit|lighthouse|headlesschrome|pagespeed|gtmetrix/i

export const isCrawler = () => {
  if (typeof navigator === 'undefined') return false
  return CRAWLER_UA.test(navigator.userAgent ?? '')
}

/**
 * The LD context for an anonymous marketing-site visitor.
 *
 * `key` is omitted on purpose: with `anonymous: true` the SDK generates a UUID and
 * persists it in localStorage itself, so a visitor keeps the same percentage bucket
 * across page views and return visits. Building our own key would duplicate that.
 *
 * Only safe to call in the browser — `navigator` is read for the crawler check.
 */
export const getLDContext = (): LDContext => ({
  kind: 'user',
  anonymous: true,
  // Exposed as a context attribute so bots can also be excluded from the LD side
  // (add a targeting rule: crawler is true -> serve false) rather than only here.
  crawler: isCrawler(),
})
